import { useCallback, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IcnClose } from '../../icons/index.js';
import { IconButton } from '../IconButton/index.js';
import { useI18nT } from '../../i18n/index.js';
import { ensurePortalRoot } from '../shared/index.js';
import { useFocusTrap } from '../../hooks/useFocusTrap.js';
import { useRestoreFocus } from '../../hooks/useRestoreFocus.js';
import { useEscapeStack } from '../../hooks/useEscapeStack.js';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock.js';

export type DrawerSide = 'left' | 'right' | 'top' | 'bottom';
export type DrawerSize = 'sm' | 'md' | 'lg' | 'auto';

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which edge the drawer slides in from. Default 'right'. */
  side?: DrawerSide;
  size?: DrawerSize;
  title?: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  /** When true, render a scrim that captures outside clicks + locks scroll. */
  modal?: boolean;
  /** Render × close button in header. Default true. */
  showClose?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  'aria-label'?: string;
}

const SIDE_SIZE: Record<DrawerSide, Record<DrawerSize, string>> = {
  right: { sm: '320px', md: '420px', lg: '560px', auto: 'min(92vw, 420px)' },
  left: { sm: '320px', md: '420px', lg: '560px', auto: 'min(92vw, 420px)' },
  top: { sm: '240px', md: '360px', lg: '520px', auto: 'min(60vh, 360px)' },
  bottom: { sm: '240px', md: '360px', lg: '520px', auto: 'min(60vh, 360px)' },
};

function sideStyle(side: DrawerSide, size: DrawerSize): Record<string, string> {
  const extent = SIDE_SIZE[side][size];
  if (side === 'left' || side === 'right') {
    return { width: extent };
  }
  return { height: extent };
}

/** Side-anchored sheet. Use for AI panels, navigation drawers, settings.
 *  - `modal` renders a scrim and traps focus
 *  - `side` picks the edge + slide direction (CSS keyframes pick it up)
 *  - `size` maps to a reasonable width/height per side
 *
 *  Focus bookkeeping delegated to the shared `useFocusTrap` + `useRestoreFocus`
 *  hooks when `modal` is true — non-modal drawers let focus flow naturally. */
export function Drawer(props: DrawerProps): ReactNode {
  const t = useI18nT();
  const {
    open,
    onOpenChange,
    side = 'right',
    size = 'md',
    title,
    subtitle,
    children,
    footer,
    modal = true,
    showClose = true,
    closeOnEscape = true,
    className,
    'aria-label': ariaLabel,
  } = props;

  const drawerRef = useRef<HTMLDivElement | null>(null);

  const reactId = useId();
  const titleId = `${reactId}-drawer-title`;
  const subtitleId = `${reactId}-drawer-subtitle`;

  const close = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  // Lock body scroll while a modal drawer is open. Reference-counted so
  // a Drawer + Dialog stack don't fight over body.style.overflow, and
  // includes scrollbar-gutter compensation to prevent layout shift.
  useBodyScrollLock(open && modal);

  useRestoreFocus(open);

  // Trap Tab focus only for modal drawers; a non-modal drawer is a regular
  // side panel — focus is allowed to leave it.
  useFocusTrap(drawerRef, { enabled: open && modal });

  // Escape via the shared stack — the topmost layer handles a single
  // Esc press, leaving outer layers (parent dialogs etc.) intact.
  useEscapeStack(close, open && closeOnEscape);

  if (!open) return null;
  const portalRoot = ensurePortalRoot();
  if (portalRoot === null) return null;

  const drawerClasses = ['vq-drawer', className].filter(Boolean).join(' ');
  const hasHeader = title !== undefined || subtitle !== undefined || showClose;

  return createPortal(
    <>
      {modal ? (
        <div className="vq-drawer-scrim" data-component="DrawerScrim" onMouseDown={close} />
      ) : null}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal={modal ? true : undefined}
        aria-labelledby={title !== undefined ? titleId : undefined}
        aria-describedby={subtitle !== undefined ? subtitleId : undefined}
        aria-label={title === undefined ? (ariaLabel ?? t('drawer.defaultLabel')) : undefined}
        tabIndex={-1}
        className={drawerClasses}
        data-component="Drawer"
        data-side={side}
        style={sideStyle(side, size)}
      >
        {hasHeader ? (
          <div className="vq-drawer__header">
            <div className="vq-drawer__header-text">
              {title !== undefined ? (
                <h2 id={titleId} className="vq-drawer__title">
                  {title}
                </h2>
              ) : null}
              {subtitle !== undefined ? (
                <p id={subtitleId} className="vq-drawer__subtitle">
                  {subtitle}
                </p>
              ) : null}
            </div>
            {showClose ? (
              <IconButton variant="ghost" size="sm" label={t('common.close')} onClick={close}>
                <IcnClose size={14} />
              </IconButton>
            ) : null}
          </div>
        ) : null}
        {children !== undefined ? <div className="vq-drawer__body">{children}</div> : null}
        {footer !== undefined ? <div className="vq-drawer__footer">{footer}</div> : null}
      </div>
    </>,
    portalRoot,
  );
}
